import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ISSUE_STATUSES } from "@/domains/pm/constants";
import { getDisciplineName } from "@/domains/pm/utils/disciplineName";
import { isProtocolFilterActive, type ProtocolIssueFilter } from "@/domains/pm/utils/protocol";
import { cn } from "@/lib/utils";
import type { Language, Translations } from "@/lib/i18n/types";
import type { Discipline, IssueStatus, PMIssue } from "@/domains/pm/types";

type ProtocolCopy = Translations["pm"]["issues"]["protocol"];

interface ProtocolIssueFiltersProps {
  /** The whole period, so the options describe what is actually here. */
  issues: PMIssue[];
  filter: ProtocolIssueFilter;
  language: Language;
  copy: ProtocolCopy;
  statusLabels: Record<IssueStatus, string>;
  onChange: (filter: ProtocolIssueFilter) => void;
}

function toggled<T>(set: ReadonlySet<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

/**
 * Narrowing the period so a long one can be worked through.
 *
 * One menu rather than a control per dimension: the step's own heading row is
 * where this belongs, and two triggers plus a clear button claimed a band of
 * their own for something used once or twice per export.
 *
 * The options come from the loaded issues, not the organisation's catalogue: a
 * discipline nobody in this period used would offer a filter that empties the
 * table, which reads as broken rather than as empty.
 */
export function ProtocolIssueFilters({
  issues,
  filter,
  language,
  copy,
  statusLabels,
  onChange,
}: ProtocolIssueFiltersProps) {
  const disciplines = new Map<number, Discipline>();
  for (const issue of issues) {
    for (const discipline of issue.disciplines ?? []) disciplines.set(discipline.id, discipline);
  }
  const disciplineOptions = Array.from(disciplines.values()).sort((a, b) =>
    getDisciplineName(a, language).localeCompare(getDisciplineName(b, language), language)
  );
  const presentStatuses = ISSUE_STATUSES.filter((status) =>
    issues.some((issue) => issue.status === status)
  );
  const active = filter.statuses.size + filter.disciplineIds.size;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("shrink-0 gap-1.5", active > 0 && "border-primary text-primary")}
        >
          <SlidersHorizontal aria-hidden className="h-3.5 w-3.5" />
          {copy.filters}
          {active > 0 && <span className="tabular-nums">({active})</span>}
          <ChevronDown aria-hidden className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>{copy.filterStatus}</DropdownMenuLabel>
        {presentStatuses.map((status) => (
          <DropdownMenuCheckboxItem
            key={status}
            checked={filter.statuses.has(status)}
            // The menu is a filter, not a command: keep it open so several can
            // be picked in one visit.
            onSelect={(event) => event.preventDefault()}
            onCheckedChange={() =>
              onChange({ ...filter, statuses: toggled(filter.statuses, status) })
            }
          >
            {statusLabels[status]}
          </DropdownMenuCheckboxItem>
        ))}

        {disciplineOptions.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{copy.filterDiscipline}</DropdownMenuLabel>
            {disciplineOptions.map((discipline) => (
              <DropdownMenuCheckboxItem
                key={discipline.id}
                checked={filter.disciplineIds.has(discipline.id)}
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={() =>
                  onChange({
                    ...filter,
                    disciplineIds: toggled(filter.disciplineIds, discipline.id),
                  })
                }
              >
                {getDisciplineName(discipline, language)}
              </DropdownMenuCheckboxItem>
            ))}
          </>
        )}

        {isProtocolFilterActive(filter) && (
          <>
            <DropdownMenuSeparator />
            {/* The one entry that IS a command, so it closes the menu. */}
            <DropdownMenuItem
              onSelect={() => onChange({ statuses: new Set(), disciplineIds: new Set() })}
            >
              {copy.filterClear}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
